/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { ChangeEvent, ChangeRejectEvent } from "./events.js";
import { AddEdge } from "./operations/add-edge.js";
import { AddNode } from "./operations/add-node.js";
import { AddModule } from "./operations/add-module.js";
import { RemoveNode } from "./operations/remove-node.js";
import { RemoveEdge } from "./operations/remove-edge.js";
import { RemoveModule } from "./operations/remove-module.js";
import { ChangeEdge } from "./operations/change-edge.js";
import { ChangeConfiguration } from "./operations/change-configuration.js";
import { ChangeMetadata } from "./operations/change-metadata.js";
import { ChangeGraphMetadata } from "./operations/change-graph-metadata.js";
import { ChangeModule } from "./operations/change-module.js";
import { GraphEditHistory } from "./history.js";
import { isImperativeGraph, toDeclarativeGraph, toImperativeGraph, } from "../run/run-imperative-graph.js";
import { AddGraph } from "./operations/add-graph.js";
import { RemoveGraph } from "./operations/remove-graph.js";
import { MutableGraphImpl } from "../inspector/graph/mutable-graph.js";
import { ToggleExport } from "./operations/toggle-export.js";
import { AddAsset } from "./operations/add-asset.js";
import { RemoveAsset } from "./operations/remove-asset.js";
import { ChangeAssetMetadata } from "./operations/change-asset-metadata.js";
import { ReplaceGraph } from "./operations/replace-graph.js";
import { ChangeEdgeMetadata } from "./operations/change-edge-metadata.js";
const validImperativeEdits = [
    "addmodule",
    "changegraphmetadata",
    "removemodule",
    "changemodule",
    "toggleexport",
];
const operations = new Map([
    ["addnode", new AddNode()],
    ["removenode", new RemoveNode()],
    ["addedge", new AddEdge()],
    ["removeedge", new RemoveEdge()],
    ["changeedge", new ChangeEdge()],
    ["changeedgemetadata", new ChangeEdgeMetadata()],
    ["changeconfiguration", new ChangeConfiguration()],
    ["changemetadata", new ChangeMetadata()],
    ["changegraphmetadata", new ChangeGraphMetadata()],
    ["addmodule", new AddModule()],
    ["removemodule", new RemoveModule()],
    ["changemodule", new ChangeModule()],
    ["addgraph", new AddGraph()],
    ["removegraph", new RemoveGraph()],
    ["toggleexport", new ToggleExport()],
    ["addasset", new AddAsset()],
    ["removeasset", new RemoveAsset()],
    ["changeassetmetadata", new ChangeAssetMetadata()],
    ["replacegraph", new ReplaceGraph()],
]);
export class Graph {
    #version = 0;
    #mutable;
    #graph;
    #eventTarget = new EventTarget();
    #history;
    #imperativeMain = null;
    constructor(mutable, options) {
        const graph = mutable.graph;
        if (isImperativeGraph(graph)) {
            this.#imperativeMain = graph.main;
            this.#graph = toDeclarativeGraph(graph);
        }
        else {
            this.#graph = graph;
        }
        this.#mutable = mutable;
        this.#version = options.version || 0;
        this.#history = new GraphEditHistory({
            graph: () => {
                return this.raw();
            },
            version: () => {
                return this.#version;
            },
            setGraph: (graph) => {
                this.#graph = graph;
                this.#version++;
                this.#mutable.rebuild(graph);
                this.#eventTarget.dispatchEvent(new ChangeEvent(this.raw(), this.#version, false, "history", [], [], [], null));
            },
            onHistoryChanged: options.onHistoryChanged,
        });
        if (options.history?.length) {
            for (const revision of options.history) {
                this.#history.add(revision.graph, revision.label, revision.creator, revision.timestamp);
            }
        }
        else {
            this.#history.add(this.raw(), "Clean slate", options.creator);
        }
    }
    #updateGraph(visualOnly, affectedNodes, affectedModules, affectedGraphs, label) {
        this.#version++;
        this.#mutable.update(this.#graph, visualOnly, affectedNodes, affectedModules);
        this.#eventTarget.dispatchEvent(new ChangeEvent(this.#graph, this.#version, visualOnly, "edit", affectedNodes, affectedModules, affectedGraphs, label));
    }
    #rollbackGraph(checkpoint, error) {
        this.#graph = checkpoint;
        this.#mutable.rebuild(this.#graph);
        this.#dispatchNoChange(error);
    }
    #dispatchNoChange(error) {
        this.#graph = { ...this.#graph };
        const reason = error
            ? {
                type: "error",
                error,
            }
            : {
                type: "nochange",
            };
        this.#eventTarget.dispatchEvent(new ChangeRejectEvent(this.raw(), reason));
    }
    addEventListener(eventName, listener) {
        this.#eventTarget.addEventListener(eventName, listener);
    }
    version() {
        return this.#version;
    }
    #shouldDiscardEdit(edit) {
        if (this.#imperativeMain) {
            return !validImperativeEdits.includes(edit.type);
        }
        return false;
    }
    async #singleEdit(edit, context) {
        const operation = operations.get(edit.type);
        if (!operation) {
            return {
                success: false,
                error: "Unsupported edit type",
            };
        }
        return operation.do(edit, context);
    }
    async edit(edits, label, dryRun = false) {
        return this.#applyEdits(async (context) => {
            await context.apply(edits, label);
            return {
                success: true,
                spec: { edits, label },
            };
        }, dryRun);
    }
    async apply(transform, dryRun = false) {
        return this.#applyEdits((context) => {
            return transform.apply(context);
        }, dryRun);
    }
    history() {
        return this.#history;
    }
    raw() {
        return this.#imperativeMain
            ? toImperativeGraph(this.#imperativeMain, this.#graph)
            : this.#graph;
    }
    inspect(id) {
        const inspectableGraph = this.#mutable.graphs.get(id || "");
        if (!inspectableGraph) {
            throw new Error(`Unknown sub-graph id: "${id}"`);
        }
        return inspectableGraph;
    }
    async #applyEdits(transformer, dryRun = false) {
        const checkpoint = structuredClone(this.#graph);
        const log = [];
        let label = "";
        let creator = { role: "unknown" };
        let error = null;
        // Presume that all edits will result in no changes.
        let noChange = true;
        // Presume that all edits will be visual only.
        let visualOnly = true;
        // Collect affected nodes
        const affectedNodes = [];
        // Collect affected modules
        const affectedModules = [];
        // Collect affected graphs
        const affectedGraphs = [];
        let context;
        const apply = async (edits, editLabel) => {
            if (error)
                return { success: false, error };
            label = editLabel;
            for (const edit of edits) {
                if (this.#shouldDiscardEdit(edit)) {
                    continue;
                }
                const result = await this.#singleEdit(edit, context);
                log.push({ edit: edit.type, result });
                if (!result.success) {
                    error = result.error;
                    return { success: false, error };
                }
                affectedNodes.push(result.affectedNodes);
                affectedModules.push(result.affectedModules);
                affectedGraphs.push(result.affectedGraphs);
                if (!result.noChange) {
                    noChange = false;
                }
                if (!result.visualOnly) {
                    visualOnly = false;
                }
                if ("creator" in edit) {
                    creator = edit.creator;
                }
            }
            return { success: true, result: undefined };
        };
        if (dryRun) {
            const graph = checkpoint;
            const mutable = new MutableGraphImpl(graph, this.#mutable.store);
            context = {
                graph,
                mutable,
                apply,
            };
        }
        else {
            context = {
                graph: this.#graph,
                mutable: this.#mutable,
                apply,
            };
        }
        const result = await transformer(context);
        if (!result.success) {
            error = result.error;
        }
        if (error) {
            !dryRun && this.#rollbackGraph(checkpoint, error);
            return { success: false, log, error };
        }
        if (noChange) {
            !dryRun && this.#dispatchNoChange();
            return { success: true, log };
        }
        this.#history.addEdit(this.raw(), checkpoint, label, this.#version, creator);
        !dryRun &&
            this.#updateGraph(visualOnly, unique(affectedNodes.flat()), [...new Set(affectedModules.flat())], [...new Set(affectedGraphs.flat())], label);
        return { success: true, log };
    }
}
function unique(affectedNodes) {
    const keys = new Set();
    return affectedNodes.filter((affectedNode) => {
        const key = `${affectedNode.id}|${affectedNode.id}`;
        if (keys.has(key))
            return false;
        keys.add(key);
        return true;
    });
}
//# sourceMappingURL=graph.js.map